/** Pure helpers for the code-editor implementation. */

export function buildVariableSnippet(namespace: string, name: string): string {
  return `{{ ${namespace}.${name} }}`;
}

export function insertAtCursor(
  text: string,
  snippet: string,
  selectionStart: number,
  selectionEnd: number,
): { text: string; caret: number } {
  const start = Math.max(0, Math.min(selectionStart, text.length));
  const end = Math.max(start, Math.min(selectionEnd, text.length));
  const nextText = text.slice(0, start) + snippet + text.slice(end);
  return { text: nextText, caret: start + snippet.length };
}

// Matches a variable output tag, tolerating trailing filters
// (`{{ heading | raw }}`, `{{ x|escape }}`) so filtered placeholders are still
// discovered and prefilled — otherwise fragments whose only content is a piped
// variable (e.g. `{{ content | raw }}`) render empty. A leading string literal
// (`{{ 'key' | trans }}`) does not start with `[\w.]`, so it is excluded.
const VARIABLE_PATTERN = /\{\{\s*([\w.]+)\s*(?:\|[^}]*)?\}\}/g;

/**
 * `{{ 'group.key' | trans }}` output tags, tolerating chained filters
 * (`{{ 'key'|trans|raw }}`) — used for the naive fallback substitution.
 */
const TRANS_PATTERN =
  /\{\{\s*['"]([^'"]+)['"]\s*\|\s*trans(?:\s*\|\s*[A-Za-z_]\w*)*\s*\}\}/g;

/**
 * Any quoted string piped into `trans`, wherever it appears — output tags,
 * `{% include … with { heading: 'key'|trans } %}` statement arguments, etc.
 * Drives translation-key extraction; match[2] is the key.
 */
const TRANS_KEY_PATTERN = /(['"])([^'"\n]+)\1\s*\|\s*trans\b/g;

/**
 * Naive preview substitution: replaces `{{ path }}` with the context value
 * when one exists and leaves everything else (unknown variables, Twig control
 * blocks like {% if %}) untouched — the preview is explicitly approximate.
 */
export function substitutePreviewVars(
  html: string,
  context: Record<string, string> = {},
): string {
  return html.replace(VARIABLE_PATTERN, (match, path: string) =>
    Object.prototype.hasOwnProperty.call(context, path) ? context[path] : match,
  );
}

/**
 * The variable paths a template actually uses (`{{ path }}`, unique, in
 * order of first appearance) — drives the test-data panel, so the fillable
 * fields always match the template's own placeholders regardless of grammar.
 */
export function extractVariablePaths(html: string): string[] {
  const paths: string[] = [];
  for (const match of html.matchAll(VARIABLE_PATTERN)) {
    if (!paths.includes(match[1])) paths.push(match[1]);
  }
  return paths;
}

/**
 * Translation keys the template uses via the `trans` filter (unique, in order
 * of first appearance) — includes keys inside `{% include … with %}` argument
 * lists and chained-filter output tags, so the translations panel always
 * mirrors what the renderer will look up.
 */
export function extractTransKeys(html: string): string[] {
  const keys: string[] = [];
  for (const match of html.matchAll(TRANS_KEY_PATTERN)) {
    if (!keys.includes(match[2])) keys.push(match[2]);
  }
  return keys;
}

/**
 * Variable paths inside a Twig **concatenation**: `'Hi ' ~ (customer.firstname)`.
 *
 * The compiler emits text that mixes words with a variable this way, because a prop is a
 * value and Twig does not re-parse a string argument. That means the path no longer
 * appears as `{{ … }}` anywhere in the body — so the preview's placeholder prefill could
 * not see it, and an unresolved variable rendered as **nothing** instead of showing
 * itself. Owner-stated: a variable should appear in the canvas "in their field manner".
 *
 * Matched only next to a `~`, and only parenthesised, which is exactly what the compiler
 * emits. A `theme.colors.brand` matched by accident costs nothing: the prefill is a
 * fallback that any real context value overrides.
 */
const CONCATENATED_PATH = /(?:~\s*\(\s*([\w.]+)\s*\)|\(\s*([\w.]+)\s*\)\s*~)/g;

export function extractConcatenatedPaths(source: string): string[] {
  const paths: string[] = [];
  for (const match of source.matchAll(CONCATENATED_PATH)) {
    const path = match[1] ?? match[2];
    // A bare identifier is a component's own prop, not a send-time path.
    if (path?.includes('.') && !paths.includes(path)) paths.push(path);
  }
  return paths;
}

/**
 * Fragment keys the source pulls in via `{% include/extends/embed/use 'key' %}`
 * (string literals only, unique, in order of first appearance). Used to warn
 * when a reference points at a fragment that isn't registered — the server's
 * DatabaseFragmentLoader would fail to resolve it at render time.
 */
const FRAGMENT_REFERENCE_PATTERN =
  /\{%-?\s*(include|extends|embed|use)\s*\(?\s*(['"])([^'"]+)\2/g;

export function extractFragmentReferences(source: string): string[] {
  const keys: string[] = [];
  for (const match of source.matchAll(FRAGMENT_REFERENCE_PATTERN)) {
    if (!keys.includes(match[3])) keys.push(match[3]);
  }
  return keys;
}

export interface FragmentReferenceRange {
  /** The referenced fragment key. */
  key: string;
  /** Character offset of the key text (inside the quotes), inclusive. */
  from: number;
  /** Character offset just past the key text. */
  to: number;
}

/**
 * Positions of every `{% include/extends/embed/use 'key' %}` reference key in
 * the source — the span of the key *inside* its quotes. Drives the editor's
 * clickable "open this building block" decorations; one entry per occurrence
 * (not deduped), since each occurrence is independently clickable.
 */
export function findFragmentReferenceRanges(
  source: string,
): FragmentReferenceRange[] {
  const ranges: FragmentReferenceRange[] = [];
  for (const match of source.matchAll(FRAGMENT_REFERENCE_PATTERN)) {
    const key = match[3];
    // The match ends with `<key><closing-quote>`, so the key occupies the
    // slice ending one char before the match end — unambiguous regardless of
    // the `include(` / quote style preceding it.
    const to = match.index! + match[0].length - 1;
    ranges.push({ key, from: to - key.length, to });
  }
  return ranges;
}

/**
 * Replaces `{{ 'group.key' | trans }}` with the translation value when one is
 * known; unknown keys stay raw, like the variable substitution above.
 */
export function substituteTransFilters(
  html: string,
  translations: Record<string, string> = {},
): string {
  return html.replace(TRANS_PATTERN, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(translations, key) &&
    translations[key] !== ''
      ? translations[key]
      : match,
  );
}

const IF_TAG_PATTERN = /\{%-?\s*(?:if|elseif)\s+([^%]+?)\s*-?%\}/g;

/** Twig/expression words that must not be mistaken for variable names. */
const CONDITION_KEYWORDS = new Set([
  'and',
  'or',
  'not',
  'is',
  'in',
  'defined',
  'iterable',
  'empty',
  'null',
  'true',
  'false',
  'same',
  'as',
  'starts',
  'ends',
  'with',
  'matches',
  'odd',
  'even',
  'length',
]);

/**
 * Variable paths referenced inside `{% if %}` / `{% elseif %}` conditions
 * (unique, in order). String literals are stripped first so quoted values
 * (`== "04"`) never count as variables. These drive editable condition
 * inputs in the test-data panel: providing a value flips the branch.
 */
/** Variable identifiers in a single condition expression (literals stripped). */
function conditionVars(condition: string): string[] {
  const stripped = condition.replace(/(['"])[^'"]*\1/g, '');
  const vars: string[] = [];
  for (const identifier of stripped.matchAll(/[A-Za-z_][\w.]*/g)) {
    const name = identifier[0];
    if (!CONDITION_KEYWORDS.has(name) && !vars.includes(name)) vars.push(name);
  }
  return vars;
}

export function extractIfConditionVars(source: string): string[] {
  const vars: string[] = [];
  for (const match of source.matchAll(IF_TAG_PATTERN)) {
    for (const name of conditionVars(match[1])) {
      if (!vars.includes(name)) vars.push(name);
    }
  }
  return vars;
}

/**
 * Removes comments that a consumed tag left empty. Authors wrap provider-side
 * merge tags in `<!-- -->` so the raw syntax cannot break the surrounding HTML
 * while editing (`<!--{% endif %}-->`); evaluating the tag away leaves
 * `<!---->` behind. Downlevel-hidden conditional comments
 * (`<!--[if mso]>…<![endif]-->`) are never empty, so they are untouched.
 */
const EMPTY_COMMENT = /<!--\s*-->/g;

export function stripEmptyComments(html: string): string {
  return html.replace(EMPTY_COMMENT, '');
}
